import { TicketHolderBookedTariff } from '@core/features/ticket-assignment/models/holder-tariff.model';
import { createSelector } from '@ngrx/store';
import { BookingTariff } from '@products/models/booking.model';
import { TariffUuid, TariffUuids, TicketPersonId } from '@products/models/tariff.model';
import { getAllBookedWorkshopTariffs } from '@store/products/booking/product/contingent/contingent-booking.selectors';
import { getAllBookedTariffs, getBookedTariff } from '@store/products/booking/product/product-booking.selectors';
import { defaultTo, filter, find, forEach, includes, indexOf, isNil, map, sortBy } from 'lodash';
import { selectBookedTariffUuids } from '../booking.selectors';

export const getAllTariffUuidsFromBookedProducts = createSelector(
  getAllBookedTariffs,
  (bookedTariffs: BookingTariff[]): TariffUuids => {
    const bookedTariffUuids: TariffUuids = [];

    bookedTariffs.forEach((bookedTariff) => bookedTariffUuids.push(...bookedTariff.tariffUuids));

    return bookedTariffUuids;
  }
);

export const getBookedTariffByTariffUuid = (tariffUuid: TariffUuid) =>
  createSelector(
    getAllBookedTariffs,
    (bookedTariffs: BookingTariff[]) =>
      bookedTariffs.find((bookedTariff) => bookedTariff.tariffUuids.includes(tariffUuid)) || null
  );

export const getBookedTariffTariffUuids = (
  ticketTypeId: number,
  ticketPersonId: number,
  voucherCode?: string,
  packageNumber?: number,
  packageIndex?: number
) =>
  createSelector(
    getBookedTariff(ticketTypeId, ticketPersonId, voucherCode, packageNumber, packageIndex),
    (bookedTariff: BookingTariff): TariffUuids => (bookedTariff ? bookedTariff.tariffUuids : [])
  );

export const getAssignedWorkshopTariffUuidsByWorkshopId = (workshopId: number) =>
  createSelector(getAllBookedWorkshopTariffs, (bookedTariffs: BookingTariff[]): TariffUuids => {
    const assignedWorkshopTariffUuids: TariffUuids = [];

    bookedTariffs.forEach((bookedTariff) => {
      bookedTariff.workshops.forEach((bookedTariffWorkshop) => {
        if (bookedTariffWorkshop.id === workshopId) {
          bookedTariffWorkshop.tariffUuids.forEach((workshopTariffUuid) =>
            assignedWorkshopTariffUuids.push(workshopTariffUuid)
          );
        }
      });
    });

    return assignedWorkshopTariffUuids;
  });

export const selectAllTicketHolderTariffUuidsFromBookedProducts = createSelector(
  getAllBookedTariffs,
  (bookedTariffs: BookingTariff[]) => {
    const ticketHolderTariffUuids: TariffUuids = [];

    forEach(bookedTariffs, (bookedTariff) => ticketHolderTariffUuids.push(...bookedTariff.tariffUuids));

    return ticketHolderTariffUuids;
  }
);

export const selectAllTicketHolderTariffUuidsWithTicketPersonId = createSelector(
  getAllBookedTariffs,
  (bookedTariffs: BookingTariff[]) => {
    const ticketHolderTariffUuidsWithTicketPersonIds: Record<TariffUuid, TicketPersonId> = {};

    forEach(bookedTariffs, ({ ticketPersonId, tariffUuids }) => {
      forEach(tariffUuids, (tariffUuid) => {
        ticketHolderTariffUuidsWithTicketPersonIds[tariffUuid] = ticketPersonId;
      });
    });

    return ticketHolderTariffUuidsWithTicketPersonIds;
  }
);

export const selectTicketPersonIdByBookedTariffUuid = (tariffUuid: TariffUuid) =>
  createSelector(
    selectAllTicketHolderTariffUuidsWithTicketPersonId,
    (tariffUuids: Record<TariffUuid, TicketPersonId>) => tariffUuids[tariffUuid]
  );

export const selectTicketHolderBookedTariffs = createSelector(
  getAllBookedTariffs,
  (allBookedTariffs): TicketHolderBookedTariff[] => {
    const ticketHolderBookedTariffs: TicketHolderBookedTariff[] = [];

    forEach(allBookedTariffs, (bookedTariff) => {
      forEach(bookedTariff.tariffUuids, (tariffUuid) => {
        const {
          ticketPersonId,
          ticketName,
          ticketTypeName,
          price,
          discountedPrice,
          discountPercentage,
          promoCodeDiscounts,
          voucherCode,
          tariffUuids,
          workshops,
          contingentReservations,
          parkingReservations
        } = bookedTariff;

        const bookedTariffUuidIndex = indexOf(tariffUuids, tariffUuid);

        const workshopNames = map(
          filter(workshops, (workshop) => includes(workshop.tariffUuids, tariffUuid)),
          ({ name }) => name
        );

        const ticketHolderBookedTariff = {
          uuid: tariffUuid,
          ticketPersonId,
          ticketName,
          ticketTypeName,
          price,
          discountedPrice,
          discountPercentage,
          promoCodeDiscounts,
          voucherCode: defaultTo(voucherCode, ''),
          workshopNames,
          contingentReservation: contingentReservations[bookedTariffUuidIndex] || null,
          parkingReservation: parkingReservations[bookedTariffUuidIndex] || null
        };

        ticketHolderBookedTariffs.push(ticketHolderBookedTariff);
      });
    });

    return ticketHolderBookedTariffs;
  }
);

export const selectTicketHolderBookedTariffsSortedByBookingOrder = createSelector(
  selectTicketHolderBookedTariffs,
  selectBookedTariffUuids,
  (allTicketHolderBookedTariffs, bookedTariffUuids): TicketHolderBookedTariff[] =>
    sortBy(allTicketHolderBookedTariffs, ({ uuid }) => indexOf(bookedTariffUuids, uuid))
);

export const selectTicketHolderBookedTariffByTariffUuids = (tariffUuids: TariffUuids) =>
  createSelector(selectTicketHolderBookedTariffs, (allTicketHolderBookedTariffs): TicketHolderBookedTariff[] => {
    const ticketHolderBookedTariffs: TicketHolderBookedTariff[] = [];

    forEach(tariffUuids, (tariffUuid) => {
      const ticketHolderBookedTariff = find(
        allTicketHolderBookedTariffs,
        (ticketHolderBookedTariff: TicketHolderBookedTariff) => ticketHolderBookedTariff.uuid === tariffUuid
      );

      if (isNil(ticketHolderBookedTariff)) {
        return;
      }

      ticketHolderBookedTariffs.push(ticketHolderBookedTariff);
    });

    return ticketHolderBookedTariffs;
  });

export const selectTicketHolderBookedTariffByTariffUuid = (tariffUuid: TariffUuid) =>
  createSelector(
    selectTicketHolderBookedTariffByTariffUuids([tariffUuid]),
    (ticketHolderBookedTariffs: TicketHolderBookedTariff[]) =>
      find(
        ticketHolderBookedTariffs,
        (ticketHolderBookedTariff: TicketHolderBookedTariff) => ticketHolderBookedTariff.uuid === tariffUuid
      )
  );
